import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  migrateLegacyVerticalCookie,
  updateVertical as updatePersistedVertical,
  VERTICAL,
} from '../helpers/verticals';
import { SectionView } from '../types';

const VerticalContext = createContext<{
  vertical: VERTICAL;
  updateVertical: (vertical: VERTICAL) => void;
}>({ vertical: VERTICAL.MOTOR, updateVertical: () => {} });

export const VerticalProvider = ({
  children,
  breadcrumbs,
  domain,
  initialVertical,
}: {
  children: React.ReactNode;
  breadcrumbs: SectionView[];
  domain: string;
  initialVertical: VERTICAL;
}) => {
  const [vertical, setVertical] = useState<VERTICAL>(initialVertical);

  const updateVertical = useCallback(
    (vertical: VERTICAL) => {
      setVertical(vertical);
      updatePersistedVertical(vertical, domain);
    },
    [domain],
  );

  useEffect(() => {
    // TODO Remove this 4 months after it is deployed (that's the max time a cookie can be stored in the browser)
    migrateLegacyVerticalCookie();

    if (breadcrumbs.length > 0) {
      updateVertical(getVerticalFromBreadcrumbs(breadcrumbs));
    }
  }, [breadcrumbs, updateVertical]);

  const contextValue = useMemo(
    () => ({ vertical, updateVertical }),
    [vertical, updateVertical],
  );

  return (
    <VerticalContext.Provider value={contextValue}>
      {children}
    </VerticalContext.Provider>
  );
};

export const useVerticalContext = () => useContext(VerticalContext);

export function getVerticalFromBreadcrumbs(
  breadcrumbs?: SectionView[],
): VERTICAL {
  const breadcrumb = breadcrumbs?.find(
    ({ name }) => name === VERTICAL.MOTOR || name === VERTICAL.FARMING,
  );
  if (breadcrumb?.name) {
    return breadcrumb.name as VERTICAL;
  } else {
    return VERTICAL.MARKETPLACE;
  }
}
